import React, { useState } from "react";
import { Search } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { INGREDIENTS, GLOSSARY_SUBHEAD } from "../data/content";

export default function Glossary() {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);

  const filtered = INGREDIENTS.filter(
    (i) =>
      i.name.toLowerCase().includes(q.toLowerCase()) ||
      i.latin.toLowerCase().includes(q.toLowerCase()) ||
      i.origin.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="pt-28" data-testid="glossary-page">
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-gold mb-4">Ingredient Glossary</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-moss leading-[0.95] max-w-3xl">
            Meet the <span className="italic text-gold">Butters and Oils.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-moss leading-relaxed font-serif italic" data-testid="glossary-subhead">
            {GLOSSARY_SUBHEAD}
          </p>
        </Reveal>

        <div className="mt-10 relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search ingredients or origin…"
            className="w-full rounded-full border border-[#E4DDD0] bg-cream py-3 pl-11 pr-4 text-sm focus:border-gold focus:outline-none"
            data-testid="glossary-search"
          />
        </div>

        <div className="mt-12 space-y-4">
          {filtered.length === 0 && (
            <p className="rounded-sm border border-[#E4DDD0] bg-cream-2 p-12 text-center text-muted" data-testid="glossary-empty">
              No ingredients match "{q}". Try a different search.
            </p>
          )}
          {filtered.map((ing, idx) => {
            const open = active === idx;
            const showOilDivider = ing.kind === "oil" && (idx === 0 || filtered[idx - 1].kind !== "oil");
            return (
              <React.Fragment key={ing.name}>
                {showOilDivider && (
                  <p className="pt-10 pb-2 text-center font-serif italic text-2xl text-moss" data-testid="glossary-oils-intro">
                    These carrier oils are selected for what they deliver to your skin.
                  </p>
                )}
                <Reveal delay={idx * 0.05}>
                <div
                  className={`rounded-sm border bg-cream transition-colors ${open ? "border-gold" : "border-[#E4DDD0]"}`}
                  data-testid={`glossary-item-${idx}`}
                >
                  <button
                    onClick={() => setActive(open ? null : idx)}
                    className="grid w-full items-center gap-4 px-6 py-6 text-left md:grid-cols-[1fr_1fr_auto]"
                  >
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-wider text-gold">{ing.tag}</p>
                      <h3 className="mt-1 font-serif text-3xl text-moss">{ing.name}</h3>
                      <p className="font-mono text-xs italic text-muted">{ing.latin}</p>
                    </div>
                    <p className="hidden text-sm text-muted md:block">{ing.origin}</p>
                    <span className="justify-self-end font-mono text-xs text-gold">{open ? "− Close" : "+ Detail"}</span>
                  </button>

                  {open && (
                    <div className="grid gap-10 border-t border-[#E4DDD0] px-6 py-8 lg:grid-cols-[1.6fr_1fr]">
                      <div>
                        <p className="font-serif text-xl text-moss leading-relaxed mb-6">{ing.intro}</p>
                        {ing.sections?.map((s) => (
                          <div key={s.title} className="mb-6">
                            <p className="font-mono text-xs uppercase tracking-wider text-gold mb-2">{s.title}</p>
                            {s.body.split("\n\n").map((para, i) => (
                              <p key={i} className="text-sm text-muted leading-relaxed mb-2">{para}</p>
                            ))}
                          </div>
                        ))}
                      </div>
                      <div>
                        <p className="font-mono text-xs uppercase tracking-wider text-gold mb-3">Benefits</p>
                        <ul className="space-y-3">
                          {ing.benefits.map((b) => (
                            <li key={b} className="flex items-start gap-2 text-sm text-moss leading-relaxed">
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" /> {b}
                            </li>
                          ))}
                        </ul>
                        {ing.usedIn && (
                          <>
                            <p className="mt-6 font-mono text-xs uppercase tracking-wider text-gold mb-2">Used in</p>
                            <p className="text-sm text-muted">{ing.usedIn.join(" · ")}</p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Reveal>
              </React.Fragment>
            );
          })}
        </div>
      </section>
    </div>
  );
}
